import * as React from 'react';

import { parseZonedDateTime } from '@rematter/common/dateTime';
import {
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
  Badge,
  LocalizedDate,
  TextArea,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

import {
  approvalRequestQueryAtom,
  ApprovalStatus,
} from '../../_graphql/approvalRequests';
import * as state from '../../_state/approvalList.state';
import { useTranslations } from '../../i18n/useTranslations';

const APPROVAL_STATUS_COLORS: Record<
  ApprovalStatus,
  'success' | 'failure' | 'warning' | 'neutral'
> = {
  Pending: 'warning',
  Approved: 'success',
  Rejected: 'failure',
  Expired: 'neutral',
};

interface ApprovalDetailDialogProps {
  approvalId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ApprovalDetailDialog: React.FC<ApprovalDetailDialogProps> = ({
  approvalId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const t = useTranslations();
  const toast = useToast();
  const [comment, setComment] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const respondToApproval = useSetAtom(state.$respondToApproval);

  const approvalQueryFactory = React.useMemo(
    () => approvalRequestQueryAtom(() => ({ variables: { id: approvalId } })),
    [approvalId],
  );
  const { data: approval, isLoading } = useAtomValue(approvalQueryFactory);

  const handleClose = () => {
    if (!isSubmitting) {
      setComment('');
      onClose();
    }
  };

  const handleRespond = async (approved: boolean) => {
    if (!approval) return;

    setIsSubmitting(true);
    try {
      await respondToApproval({
        id: approvalId,
        input: {
          approved,
          comment: comment.trim() || null,
        },
      });

      toast.success(
        approved ? t.approvals.approvedSuccess : t.approvals.rejectedSuccess,
      );
      onSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.approvals.respondFailed,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPending = approval?.status === 'Pending';
  const isExpired = approval?.status === 'Expired';

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      className="@ApprovalDetailDialog"
    >
      <DialogContent
        position="center"
        className="w-full max-w-2xl min-w-[32rem]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <AlertCircle className="size-5" />
            {t.approvals.title}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="p-8 text-center text-neutral/60">
            {t.approvals.loading}
          </div>
        ) : approval ? (
          <div className="max-h-[60vh] space-y-6 overflow-y-auto p-4">
            {isExpired && (
              <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/10 p-4">
                <AlertCircle className="mt-0.5 size-5 shrink-0 text-warning" />
                <div>
                  <p className="font-medium text-warning">
                    {t.approvals.requestExpired}
                  </p>
                  <p className="mt-1 text-sm text-warning/80">
                    {t.approvals.requestExpiredDescription}
                  </p>
                </div>
              </div>
            )}

            {!isPending && !isExpired && (
              <div className="flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/10 p-4">
                <AlertCircle className="mt-0.5 size-5 shrink-0 text-primary" />
                <div>
                  <p className="font-medium text-primary">
                    {t.approvals.alreadyResponded}
                  </p>
                  <p className="mt-1 text-sm text-primary/80">
                    {t.approvals.alreadyRespondedDescription(
                      approval.status.toLowerCase(),
                    )}
                  </p>
                </div>
              </div>
            )}

            <div className="grid gap-4">
              <div>
                <label className="text-sm font-medium text-neutral/60">
                  {t.approvals.status}
                </label>
                <div className="mt-1">
                  <Badge color={APPROVAL_STATUS_COLORS[approval.status]}>
                    {t.approvals.statusLabels[approval.status]}
                  </Badge>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral/60">
                  {t.approvals.approvalStep}
                </label>
                <div className="mt-1 text-neutral">
                  {approval.nodeLabel || t.approvals.notAvailable}
                </div>
              </div>

              {approval.instructions && (
                <div>
                  <label className="text-sm font-medium text-neutral/60">
                    {t.approvals.instructions}
                  </label>
                  <div className="mt-1 text-neutral">
                    {approval.instructions}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-neutral/60">
                    {t.approvals.requestedAt}
                  </label>
                  <div className="mt-1 text-neutral">
                    <LocalizedDate
                      date={parseZonedDateTime(approval.requestedAt)}
                    />
                  </div>
                </div>

                {approval.expiresAt && (
                  <div>
                    <label className="text-sm font-medium text-neutral/60">
                      {t.approvals.expiresAt}
                    </label>
                    <div className="mt-1 text-neutral">
                      <LocalizedDate
                        date={parseZonedDateTime(approval.expiresAt)}
                      />
                    </div>
                  </div>
                )}
              </div>

              {approval.respondedAt && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-neutral/60">
                      {t.approvals.respondedAt}
                    </label>
                    <div className="mt-1 text-neutral">
                      <LocalizedDate
                        date={parseZonedDateTime(approval.respondedAt)}
                      />
                    </div>
                  </div>

                  {approval.respondedBy && (
                    <div>
                      <label className="text-sm font-medium text-neutral/60">
                        {t.approvals.respondedBy}
                      </label>
                      <div className="mt-1 text-neutral">
                        {approval.respondedBy}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {approval.comment && (
                <div>
                  <label className="text-sm font-medium text-neutral/60">
                    {t.approvals.responseComment}
                  </label>
                  <div className="mt-1 text-neutral">{approval.comment}</div>
                </div>
              )}

              {approval.executionData &&
                Object.keys(approval.executionData).length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-neutral/60">
                      {t.approvals.executionContext}
                    </label>
                    <div className="mt-1 rounded-lg border border-neutral/20 bg-neutral/5 p-3">
                      <pre className="text-xs text-neutral/80">
                        {JSON.stringify(approval.executionData, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

              {isPending && (
                <div>
                  <label
                    htmlFor="comment"
                    className="text-sm font-medium text-neutral/60"
                  >
                    {t.approvals.addComment}
                  </label>
                  <TextArea
                    id="comment"
                    value={comment}
                    onChange={(value) => setComment(value)}
                    placeholder={t.approvals.commentPlaceholder}
                    rows={3}
                    className="mt-1"
                    isDisabled={isSubmitting}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-failure">
            {t.approvals.loadFailed}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {isPending ? t.approvals.cancel : t.approvals.close}
          </Button>

          {isPending && approval && (
            <>
              <Button
                variant="solid"
                color="failure"
                onPress={() => handleRespond(false)}
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                icon={<XCircle className="size-4" />}
              >
                {t.approvals.reject}
              </Button>
              <Button
                variant="solid"
                color="success"
                onPress={() => handleRespond(true)}
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                icon={<CheckCircle className="size-4" />}
              >
                {t.approvals.approve}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
